import React, { Component } from 'react';
import PropTypes from 'prop-types';
import BEM from '../bem';
import propTypes from '../propTypes';
import mobxInjectSelect from '../utils/mobxInjectSelect';
import { ReactComponent as MoreButtonSvg } from '../images/more-button.svg';
import { ReactComponent as FlagIcon } from '../images/flag.svg';
import { ReactComponent as PinIcon } from '../images/pin.svg';
import { ContextMenu, MenuItemList, MenuItem } from './ContextMenu';
import ReduxEscapeHatch from 'common/components/ReduxEscapeHatch';

const classes = BEM.with('ConversationOptionMenu');

class ConversationOptionMenu extends Component {
  static propTypes = {
    conversation: propTypes.conversation.isRequired,
    isSelected: PropTypes.bool.isRequired,
    openModal: PropTypes.func.isRequired,
    showLeaveGroupModal: PropTypes.func.isRequired,
    showMuteModal: PropTypes.func.isRequired,
    unmuteConversation: PropTypes.func.isRequired,
    isFlagConversationEnabled: PropTypes.bool.isRequired,
    toggleFlagConversation: PropTypes.func.isRequired,
    isPinConversationEnabled: PropTypes.bool,
    togglePinConversation: PropTypes.func.isRequired,
    selectedFilter: PropTypes.string.isRequired,
    logPinConversation: PropTypes.func.isRequired,
    logFlagConversation: PropTypes.func.isRequired,
    showDeleteConversationModal: PropTypes.func.isRequired,
  };

  state = {
    isDeleteConversationModalOpen: false,
  };

  render() {
    const { conversation, isSelected, isFlagConversationEnabled, isPinConversationEnabled } =
      this.props;
    const {
      counterParty,
      counterPartyType,
      isDeletable,
      isMuteable,
      isMuted,
      isFlagged,
      isPinned,
    } = conversation;
    const { groupType = 'GROUP', hasCurrentUser = false } = counterParty || {};
    const canShowConversationActions =
      conversation.network !== 'PATIENT' &&
      !(counterPartyType === 'group' && groupType === 'FORUM' && !hasCurrentUser);

    const showFlagMenu = isFlagConversationEnabled && canShowConversationActions;
    const showPinMenu = isPinConversationEnabled && canShowConversationActions;

    if (!isMuteable && !isDeletable && !showFlagMenu && !showPinMenu)
      return <div className={classes()} />;

    let deleteFragment;
    if (isDeletable) {
      if (counterPartyType === 'group') {
        if (groupType === 'ROLE_P2P') {
          deleteFragment = (
            <MenuItem
              onClick={this._showLeaveGroupModal}
              ariaLabel="Delete Role"
              dataTestId="delete-role-menu-item"
            >
              Delete
            </MenuItem>
          );
        } else if (groupType === 'FORUM' && !hasCurrentUser) {
          deleteFragment = (
            <MenuItem
              onClick={this._showJoinForumModal}
              ariaLabel="Info Pane Join Group"
              dataTestId="join-forum-menu-item"
            >
              Join
            </MenuItem>
          );
        } else if (groupType !== 'INTRA_TEAM') {
          deleteFragment = (
            <MenuItem
              onClick={this._showLeaveGroupModal}
              ariaLabel="Info Pane Leave Group"
              dataTestId="leave-group-menu-item"
            >
              Leave
            </MenuItem>
          );
        }
      } else {
        deleteFragment = (
          <MenuItem
            onClick={this._showDeleteConversationModal}
            ariaLabel="Delete Conversation"
            dataTestId="delete-conversation-menu-item"
          >
            Delete
          </MenuItem>
        );
      }
    }

    let flagConversationFragment;
    if (showFlagMenu) {
      flagConversationFragment = (
        <MenuItem
          onClick={this._onFlagButtonClick}
          className={classes('option-flag', { active: isFlagged })}
          ariaLabel={conversation.isFlagged ? 'Unflag conversation' : 'Flag conversation'}
          dataTestId="conversation-flag-button"
        >
          <FlagIcon />
          {conversation.isFlagged ? 'Unflag' : 'Flag'}
        </MenuItem>
      );
    }

    let pinConversationFragment;
    if (showPinMenu) {
      pinConversationFragment = (
        <MenuItem
          onClick={this._onPinButtonClick}
          className={classes('option-pin', { active: isPinned })}
          ariaLabel={conversation.isPinned ? 'Unpin conversation' : 'Pin conversation'}
          dataTestId="conversation-pin-button"
        >
          <PinIcon />
          {conversation.isPinned ? 'Unpin' : 'Pin'}
        </MenuItem>
      );
    }

    return (
      <div className={classes()} data-test-id="conversation-option-menu">
        <div className={classes('option-button')} data-test-id="conversation-option-menu-button">
          <ContextMenu
            event="click"
            position="bottomright"
            hideOnScroll={true}
            data-test-id="context-menu-portal"
            menu={
              <MenuItemList>
                {isMuteable && (
                  <MenuItem ariaLabel="Mute Modal Conversation" onClick={this._onMuteButtonClick}>
                    {isMuted ? 'Unmute' : 'Mute'}
                  </MenuItem>
                )}
                {deleteFragment}
                {flagConversationFragment}
                {pinConversationFragment}
              </MenuItemList>
            }
          >
            <MoreButtonSvg className={classes({ isSelected })} />
          </ContextMenu>
        </div>
        <ReduxEscapeHatch
          ref={(ref) => {
            this.reduxEscapeHatch = ref;
          }}
        />
      </div>
    );
  }

  _onPinButtonClick = (e) => {
    e.stopPropagation();
    const { conversation, togglePinConversation, logPinConversation } = this.props;
    logPinConversation(conversation);
    togglePinConversation(conversation);
  };

  _onFlagButtonClick = (e) => {
    e.stopPropagation();
    const { conversation, toggleFlagConversation, logFlagConversation, selectedFilter } =
      this.props;
    logFlagConversation(conversation, selectedFilter);
    toggleFlagConversation(conversation);
  };

  _onMuteButtonClick = () => {
    const { conversation, showMuteModal, unmuteConversation } = this.props;
    if (conversation.isMuted) {
      unmuteConversation(conversation.id);
      this.reduxEscapeHatch.focusMessageBodyInput();
    } else {
      showMuteModal(conversation);
    }
  };

  _showDeleteConversationModal = () => {
    const { conversation, showDeleteConversationModal } = this.props;
    showDeleteConversationModal(conversation);
  };

  _showJoinForumModal = () => {
    const { conversation, openModal } = this.props;
    const { counterParty } = conversation;
    openModal('joinForum', { conversation, forum: counterParty });
  };

  _showLeaveGroupModal = () => {
    const { conversation, showLeaveGroupModal } = this.props;
    const { counterParty } = conversation;
    showLeaveGroupModal(counterParty);
  };
}

export default mobxInjectSelect({
  conversationStore: [
    'showDeleteConversationModal',
    'showMuteModal',
    'unmuteConversation',
    'toggleFlagConversation',
    'togglePinConversation',
  ],
  groupEditorStore: ['showLeaveGroupModal'],
  modalStore: ['openModal'],
  trackerStore: ['logPinConversation', 'logFlagConversation'],
  messengerStore: ['isFlagConversationEnabled', 'isPinConversationEnabled'],
  rosterStore: ['selectedFilter'],
})(ConversationOptionMenu);
